import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import assessmentService from '@/services/assessmentService';
import type { AssessmentScores, AssessmentNotes, PublicAssessment } from '@arrowise/shared';
import { createAssessmentSchema } from '@arrowise/shared';

interface AssessmentFormData {
    scores: AssessmentScores;
    notes: AssessmentNotes;
    overallNote?: string;
}

interface AssessmentFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sessionId: string;
    existingAssessment?: PublicAssessment | null;
    onSuccess?: () => void;
}

const categories = [
    { key: 'aiLiteracy' as keyof AssessmentScores, label: 'AI Literacy', description: 'How effectively the candidate uses AI tools' },
    { key: 'codeOrganization' as keyof AssessmentScores, label: 'Code Organization', description: 'Code structure, readability, and file naming' },
    { key: 'problemUnderstanding' as keyof AssessmentScores, label: 'Problem Understanding', description: 'Grasp of requirements and problem analysis' },
    { key: 'solutionQuality' as keyof AssessmentScores, label: 'Solution Quality', description: 'Correctness, efficiency, and approach' },
    { key: 'communication' as keyof AssessmentScores, label: 'Communication', description: 'Clarity in prompts and interaction' },
    { key: 'creativity' as keyof AssessmentScores, label: 'Creativity', description: 'Innovation and problem-solving approach' },
    { key: 'efficiency' as keyof AssessmentScores, label: 'Efficiency', description: 'Time management and productivity' },
];

const AssessmentFormDialog: React.FC<AssessmentFormDialogProps> = ({
    open,
    onOpenChange,
    sessionId,
    existingAssessment,
    onSuccess
}) => {
    const [loading, setLoading] = useState(false);

    // Get initial values based on existing assessment or defaults
    const getInitialValues = (): AssessmentFormData => {
        if (existingAssessment) {
            return {
                scores: existingAssessment.scores,
                notes: existingAssessment.notes,
                overallNote: existingAssessment.overallNote || '',
            };
        }
        return {
            scores: {
                aiLiteracy: null,
                codeOrganization: null,
                problemUnderstanding: null,
                solutionQuality: null,
                communication: null,
                creativity: null,
                efficiency: null,
            },
            notes: {
                aiLiteracy: undefined,
                codeOrganization: undefined,
                problemUnderstanding: undefined,
                solutionQuality: undefined,
                communication: undefined,
                creativity: undefined,
                efficiency: undefined,
            },
            overallNote: '',
        };
    };

    const handleSubmit = async (values: AssessmentFormData, { setSubmitting }: any) => {
        setLoading(true);
        try {
            // Validate that at least one score is provided
            const hasScores = Object.values(values.scores).some(score => score !== null);
            if (!hasScores) {
                toast.error('Please provide at least one score');
                return;
            }

            // Clean up the data: convert empty strings to undefined
            const cleanedData = {
                scores: values.scores,
                notes: Object.fromEntries(
                    Object.entries(values.notes).filter(([_, value]) => value && value.trim() !== '')
                ),
                overallNote: values.overallNote && values.overallNote.trim() !== '' ? values.overallNote : undefined,
            };

            await assessmentService.createOrUpdateAssessment(sessionId, cleanedData);

            toast.success(existingAssessment ? 'Assessment updated successfully' : 'Assessment created successfully');
            onSuccess?.();
        } catch (error: any) {
            toast.error(error.message || 'Failed to save assessment');
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const renderStars = (score: number | null) => {
        if (score === null) return <span className="text-xs text-muted-foreground">Move the slider to give the candidate<br />a score for this category</span>;
        return (
            <div className="flex items-center gap-1">
                {Array.from({ length: 10 }, (_, i) => (
                    <Star
                        key={i}
                        className={`w-3 h-3 ${i < score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                ))}
                <span className="text-sm font-medium ml-1">{score}/10</span>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {existingAssessment ? 'Edit Assessment' : 'Assess Candidate Session'}
                    </DialogTitle>
                    <DialogDescription>
                        Rate the candidate's performance across different categories. Scores are from 1-10, with 10 being excellent.
                    </DialogDescription>
                </DialogHeader>

                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={createAssessmentSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form className="space-y-6">
                            {/* Scoring Categories */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Category Scores</h3>
                                {categories.map((category) => (
                                    <Card key={category.key}>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-base flex items-center justify-between">
                                                <span>{category.label}</span>
                                                {renderStars(values.scores[category.key])}
                                            </CardTitle>
                                            <p className="text-sm text-muted-foreground">{category.description}</p>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            <div className="space-y-2">
                                                <Label className="text-sm">Score (1-10)</Label>
                                                <Slider
                                                    value={values.scores[category.key] !== null ? [values.scores[category.key]!] : [1]}
                                                    onValueChange={(value) => setFieldValue(`scores.${category.key}`, value[0])}
                                                    max={10}
                                                    min={1}
                                                    step={1}
                                                    className="w-full"
                                                />
                                                <div className="flex justify-between text-xs text-muted-foreground">
                                                    <span>Poor (1)</span>
                                                    <span>Excellent (10)</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setFieldValue(`scores.${category.key}`, null)}
                                                        className="text-xs text-muted-foreground hover:text-destructive"
                                                    >
                                                        Clear score
                                                    </button>
                                                </div>
                                                <ErrorMessage name={`scores.${category.key}`} component="div" className="text-xs text-destructive" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm">Notes (Optional)</Label>
                                                <Field
                                                    as={Textarea}
                                                    name={`notes.${category.key}`}
                                                    placeholder={`Add notes about ${category.label.toLowerCase()}...`}
                                                    rows={2}
                                                    className="resize-none"
                                                />
                                                <ErrorMessage name={`notes.${category.key}`} component="div" className="text-xs text-destructive" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            <Separator />

                            {/* Overall Note */}
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">Overall Assessment</Label>
                                <Field
                                    as={Textarea}
                                    name="overallNote"
                                    placeholder="Add any overall comments about the candidate's performance..."
                                    rows={4}
                                    className="resize-none"
                                />
                                <ErrorMessage name="overallNote" component="div" className="text-xs text-destructive" />
                            </div>

                            {/* Actions */}
                            <div className="sticky bottom-0 p-4">
                                <div className="flex justify-end gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => onOpenChange(false)}
                                        disabled={isSubmitting || loading}
                                    >
                                        Close
                                    </Button>
                                    <Button type="submit" disabled={isSubmitting || loading}>
                                        {(isSubmitting || loading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default AssessmentFormDialog;
